import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PsychologyIcon from "@mui/icons-material/Psychology";
import LightbulbIcon from "@mui/icons-material/Lightbulb";

const BookmarkNavigation = ({
  isLoggedIn,
  relevanceResult,
  loadingButton,
  loading,
  checkSkill,
  poweredByChatGPT,
  chatgptError,
  chatGptButton,
  chatGPTData,
  aiToolsData,
  aiToolsError,
  disruptionData,
  disruptionError,
}) => {
  const [activeSection, setActiveSection] = useState("");
  const [isAtTop, setIsAtTop] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isXL = useMediaQuery("(min-width:1200px)"); // XL and above

  // Function to render custom SVG icons
  const renderCustomIcon = (iconName, size = 24) => {
    const iconPath = `/assets/images/bookmark-icons/${iconName}.svg`;
    return (
      <img
        src={iconPath}
        alt={iconName}
        style={{
          width: size,
          height: size,
          objectFit: "contain",
        }}
        onError={(e) => {
          // Fallback to Material-UI icon if custom icon fails to load
          console.warn(`Failed to load custom icon: ${iconName}`);
          e.target.style.display = "none";
        }}
      />
    );
  };

  // Define sections for bookmarks with custom SVG icons - reactive to screen size
  const sections = useMemo(() => [
    { id: "enter-your-skills", label: "Search Anything", icon: renderCustomIcon("enter-skills") },
    { id: "relevance-result", label: "Relevance Result", icon: renderCustomIcon("relevance-result") },
    // { id: "chat-gpt-result", label: "Chat GPT Result", icon: renderCustomIcon("chat-GPT-result") },
    { id: "insights-group", label: "Sub Skills & Insights", icon: renderCustomIcon("sub-skills") },
    { id: "skill-role-disruption-section", label: "Skill & Role Disruption", icon: <PsychologyIcon sx={{ fontSize: 20, color: "#805AD5" }} /> },
    { id: "survival-strategy-section", label: "Survival Strategy", icon: <LightbulbIcon sx={{ fontSize: 20, color: "#FFD700" }} /> },
    { id: "ai-tools-section", label: "Productivity Tools", icon: <RocketLaunchIcon sx={{ fontSize: 20, color: "#DD6B20" }} /> },
    // Conditional sections based on screen size
    ...(isXL ? [
      { id: "roles-group", label: "Roles & Courses", icon: renderCustomIcon("suggested-roles") }
    ] : [
      { id: "suggested-roles", label: "Suggested Roles", icon: renderCustomIcon("suggested-roles") },
      { id: "other-roles-with-responsibilities", label: "Other Roles with Responsibilities", icon: renderCustomIcon("other-roles-with-responsibilities") },
      { id: "courses", label: "Courses", icon: renderCustomIcon("courses") }
    ]),
    { id: "higher-roles-with-promotions", label: "Career Path", icon: renderCustomIcon("higher-roles") },
    { id: "salary-insights", label: "Salary Insights", icon: renderCustomIcon("salary-insights") },
    // { id: "job-suggestions", label: "Job Suggestions", icon: renderCustomIcon("job-suggestions") },
    { id: "interview-questions", label: "Interview Questions", icon: renderCustomIcon("interview-questions") },
    { id: "career-disruptors", label: "Game-Changers", icon: renderCustomIcon("game-changer") },
    // { id: "google-trends", label: "Google Trends", icon: <TrendingUpIcon /> },
  ], [isXL]);

  // Function to scroll to section
  const scrollToSection = (sectionId) => {
    let element;
    
    // Handle grouped sections for XL devices
    if (sectionId === "insights-group") {
      // For XL devices, scroll to the first section in the group (sub-skills)
      element = document.getElementById("sub-skills");
    } else if (sectionId === "roles-group") {
      // For XL devices, scroll to the first section in the roles group (suggested-roles)
      element = document.getElementById("suggested-roles");
    } else {
      element = document.getElementById(sectionId);
    }
    
    if (element) {
      // Get the element's position relative to the viewport
      const elementRect = element.getBoundingClientRect();
      const elementTop = elementRect.top + window.pageYOffset;
      
      // Add offset for header (assuming header height is around 80px, adjust as needed)
      const headerOffset = 100;
      const offsetPosition = elementTop - headerOffset;
      
      // Smooth scroll to the calculated position
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      
      setActiveSection(sectionId);
    }
  };

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActiveSection("top");
  };

  // Function to check if a section is rendered/available
  const isSectionRendered = (sectionId) => {
    // First check if relevance result is available - if not, don't show any subsequent sections
    const hasRelevanceResult = (
      relevanceResult?.length !== 0 &&
      relevanceResult !== undefined &&
      !loadingButton &&
      loading === false &&
      isLoggedIn
    );

    switch (sectionId) {
      case "enter-your-skills":
        return isLoggedIn;
      case "relevance-result":
        return hasRelevanceResult;
      case "chat-gpt-result":
        return (
          hasRelevanceResult &&
          checkSkill !== "" &&
          poweredByChatGPT &&
          !chatgptError
        );
      case "insights-group":
        // This grouped section is rendered when any of the individual sections are available
        return (
          hasRelevanceResult &&
          !chatGptButton &&
          checkSkill !== "" &&
          poweredByChatGPT &&
          !chatgptError &&
          chatGPTData
        );
      case "roles-group":
        // This grouped section is rendered when any of the individual sections are available
        return (
          hasRelevanceResult &&
          !chatGptButton &&
          checkSkill !== "" &&
          poweredByChatGPT &&
          !chatgptError &&
          chatGPTData
        );
      case "sub-skills":
      case "enterprise-adoption":
      case "learn-earn":
      case "suggested-roles":
      case "other-roles-with-responsibilities":
      case "courses":
      case "higher-roles-with-promotions":
      case "salary-insights":
      // case "job-suggestions":
      case "interview-questions":
      case "career-disruptors":
        // case "google-trends":
        // These sections are rendered by GetInsights component
        return (
          hasRelevanceResult &&
          !chatGptButton &&
          checkSkill !== "" &&
          poweredByChatGPT &&
          !chatgptError &&
          chatGPTData
        );
      case "skill-role-disruption-section":
        return (
          hasRelevanceResult &&
          !chatGptButton &&
          checkSkill !== "" &&
          poweredByChatGPT &&
          !chatgptError &&
          chatGPTData &&
          disruptionData &&
          !disruptionError
        );
      case "survival-strategy-section":
        return (
          hasRelevanceResult &&
          !chatGptButton &&
          checkSkill !== "" &&
          poweredByChatGPT &&
          !chatgptError &&
          chatGPTData &&
          disruptionData &&
          !disruptionError
        );
      case "ai-tools-section":
        // Only show AI Tools bookmark when the section is actually rendered
        // (API succeeded and data is available)
        return (
          hasRelevanceResult &&
          !chatGptButton &&
          checkSkill !== "" &&
          poweredByChatGPT &&
          !chatgptError &&
          chatGPTData &&
          aiToolsData &&
          !aiToolsError
        );
      default:
        return false;
    }
  };

  // Filter sections to only show rendered ones - reactive to screen size
  const renderedSections = useMemo(() => 
    sections.filter(section => isSectionRendered(section.id)), 
    [sections, isLoggedIn, relevanceResult, loadingButton, loading, checkSkill, poweredByChatGPT, chatgptError, chatGptButton, chatGPTData, aiToolsData, aiToolsError, disruptionData, disruptionError]
  );

  // Set initial active section when sections are rendered
  useEffect(() => {
    if (renderedSections.length > 0 && !activeSection) {
      setActiveSection(renderedSections[0].id);
    }
  }, [renderedSections, activeSection]);

  // Intersection Observer to track active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Handle grouped section logic
            if (entry.target.id === "sub-skills" || 
                entry.target.id === "enterprise-adoption" || 
                entry.target.id === "learn-earn") {
              // If any of the grouped sections are visible, set active to insights-group
              setActiveSection("insights-group");
            } else if (entry.target.id === "suggested-roles" ||
                       entry.target.id === "other-roles-with-responsibilities" ||
                       entry.target.id === "courses") {
              // Handle roles/courses sections based on screen size
              if (isXL) {
                // For XL devices, group them together
                setActiveSection("roles-group");
              } else {
                // For smaller devices, use individual section IDs
                setActiveSection(entry.target.id);
              }
            } else {
              setActiveSection(entry.target.id);
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    // Observe all sections including individual sections within the groups
    const sectionsToObserve = [
      ...renderedSections.map(section => section.id),
      // Also observe individual sections within the groups
      "sub-skills",
      "enterprise-adoption", 
      "learn-earn",
      "suggested-roles",
      "other-roles-with-responsibilities",
      "courses"
    ];

    sectionsToObserve.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [renderedSections, isXL]);

  // Scroll event listener to track if user is at top
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsAtTop(scrollTop <= 50); // Consider "at top" if within 50px of the top
    };

    // Set initial state
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle clicking outside mobile menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const mobileNav = document.getElementById("mobile-nav-expanded");
      const mobileBall = event.target.closest('[data-mobile-nav-ball]');
      
      if (isMobileMenuOpen && mobileNav && !mobileNav.contains(event.target) && !mobileBall) {
        setIsMobileMenuOpen(false);
        mobileNav.style.display = "none";
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Don't render if no sections are available
  if (renderedSections.length === 0) {
    return null;
  }

  return (
    <>
      {/* Desktop Bookmark Navigation Panel - Collapsible with hover */}
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          right: 16,
          transform: "translateY(-50%)",
          zIndex: 999,
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          border: "1px solid #e0e0e0",
          p: 1,
          maxHeight: "80vh",
          overflowY: "auto",
          display: { xs: "none", md: "block" },
          transition: "all 0.3s ease",
          "&:hover": {
            width: "auto",
            minWidth: "200px",
            "& .navigation-title": {
              opacity: 1,
              visibility: "visible",
            },
            "& .navigation-label": {
              opacity: 1,
              visibility: "visible",
              display: "block",
            },
            "& .hover-indicator": {
              opacity: 0,
            },
          },
          width: "60px",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            left: "-8px",
            top: "50%",
            transform: "translateY(-50%)",
            width: 0,
            height: 0,
            borderTop: "6px solid transparent",
            borderBottom: "6px solid transparent",
            borderRight: "8px solid #6652E4",
            opacity: 0.6,
            transition: "opacity 0.3s ease",
          },
          "&:hover::before": {
            opacity: 0,
          },
        }}
      >
        {/* <Typography
          variant="h6"
          className="navigation-title"
          sx={{
            fontFamily: "poppins",
            fontWeight: 600,
            color: "#6652E4",
            mb: 2,
            textAlign: "center",
            fontSize: "14px",
            whiteSpace: "nowrap",
            opacity: 0,
            visibility: "hidden",
            transition: "opacity 0.3s ease, visibility 0.3s ease",
          }}
        >
          Quick Navigation
        </Typography> */}

        {/* Hover indicator for collapsed state */}
        <Box
          className="hover-indicator"
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            width: "4px",
            height: "4px",
            borderRadius: "50%",
            background: "#6652E4",
            opacity: 0.4,
            transition: "opacity 0.3s ease",
          }}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Scroll to Top Button - Only show when not at top */}
          {!isAtTop && (
            <>
              <Box
                onClick={scrollToTop}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  borderRadius: "8px",
                  background:
                    activeSection === "top"
                      ? "#f0f0ff"
                      : "transparent",
                  border:
                    activeSection === "top"
                      ? `2px solid #6652E4`
                      : `1px solid transparent`,
                  color:
                    activeSection === "top"
                      ? "#6652E4"
                      : "#333",
                  fontWeight: activeSection === "top" ? 600 : 400,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: "#f8f8ff",
                    border: `2px solid #6652E4`,
                    transform: "translateX(-4px)",
                  },
                  minHeight: "40px",
                  justifyContent: "flex-start",
                }}
              >
                <Box
                  sx={{
                    fontSize: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "24px",
                    width: "24px",
                    height: "24px",
                    "& img": {
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    },
                    "& svg": {
                      width: "100%",
                      height: "100%",
                    },
                  }}
                >
                  <KeyboardArrowUpIcon />
                </Box>
                <Typography
                  className="navigation-label"
                  sx={{
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    opacity: 0,
                    visibility: "hidden",
                    display: "none",
                    transition: "opacity 0.3s ease, visibility 0.3s ease, display 0.3s ease",
                    fontWeight: activeSection === "top" ? 600 : 400,
                  }}
                >
                  Back to Top
                </Typography>
              </Box>

              {/* Divider */}
              <Box
                sx={{
                  height: "1px",
                  background: "#e0e0e0",
                  my: 0.5,
                  opacity: 0.5,
                }}
              />
            </>
          )}

          {renderedSections.map((section) => (
            <Box
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                borderRadius: "8px",
                background:
                  activeSection === section.id
                    ? "#f0f0ff"
                    : "transparent",
                border:
                  activeSection === section.id
                    ? `2px solid #6652E4`
                    : `1px solid transparent`,
                color:
                  activeSection === section.id
                    ? "#6652E4"
                    : "#333",
                fontWeight: activeSection === section.id ? 600 : 400,
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "#f8f8ff",
                  border: `2px solid #6652E4`,
                  transform: "translateX(-4px)",
                },
                minHeight: "40px",
                justifyContent: "flex-start",
              }}
            >
              <Box
                sx={{
                  fontSize: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "24px",
                  width: "24px",
                  height: "24px",
                  "& img": {
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  },
                  "& svg": {
                    width: "100%",
                    height: "100%",
                  },
                }}
              >
                {section.icon}
              </Box>
              <Typography
                className="navigation-label"
                sx={{
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  opacity: 0,
                  visibility: "hidden",
                  display: "none",
                  transition: "opacity 0.3s ease, visibility 0.3s ease, display 0.3s ease",
                  fontWeight: activeSection === section.id ? 600 : 400,
                }}
              >
                {section.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Mobile Bookmark Navigation - iPhone-style floating ball */}
      <Box
        sx={{
          position: "fixed",
          bottom: 15,
          right: 15,
          zIndex: 999,
          display: { xs: "block", md: "none" },
          userSelect: "none",
          touchAction: "none",
          transform: "translate3d(0, 0, 0)", // Force hardware acceleration
        }}
      >
        {/* Main floating ball */}
        <Box
          data-mobile-nav-ball
          sx={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #6652E4 0%, #8B7CF6 100%)",
            boxShadow: "0 8px 32px rgba(102, 82, 228, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer", // Removed isDragging
            transition: "all 0.2s ease",
            transform: "scale(1)", // Removed isDragging
            willChange: "transform",
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: "0 12px 40px rgba(102, 82, 228, 0.4)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
          }}
          onClick={(e) => {
            const expandedMenu = document.getElementById("mobile-nav-expanded");
            if (expandedMenu) {
              const newState = !isMobileMenuOpen;
              setIsMobileMenuOpen(newState);
              expandedMenu.style.display = newState ? "block" : "none";
            }
          }}
        >
          <Box
            sx={{
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"
                fill="currentColor"
              />
            </svg>
          </Box>
        </Box>

        {/* Expanded menu - initially hidden */}
        <Box
          id="mobile-nav-expanded"
          sx={{
            position: "absolute",
            bottom: "70px",
            right: "0", // Changed from right: "0" to left: "0"
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "16px",
            padding: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            display: "none",
            minWidth: "200px",
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {/* <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "poppins",
              fontWeight: 600,
              color: "#6652E4",
              mb: 2,
              textAlign: "center",
              fontSize: "12px",
            }}
          >
            Quick Navigation
          </Typography> */}

          {/* Scroll to Top Button */}
          {!isAtTop && (
            <Box
              onClick={scrollToTop}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: "8px",
                background: activeSection === "top" ? "#f0f0ff" : "transparent",
                color: activeSection === "top" ? "#6652E4" : "#333",
                fontWeight: activeSection === "top" ? 600 : 400,
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "#f8f8ff",
                  transform: "translateX(-2px)",
                },
                mb: 1,
              }}
            >
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "inherit",
                }}
              >
                <KeyboardArrowUpIcon sx={{ fontSize: "18px" }} />
              </Box>
              <Typography
                sx={{
                  fontSize: "14px", // Increased from "11px"
                  fontWeight: "inherit",
                }}
              >
                Back to Top
              </Typography>
            </Box>
          )}

          {/* Section navigation items */}
          {renderedSections.map((section) => (
            <Box
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: "8px",
                background: activeSection === section.id ? "#f0f0ff" : "transparent",
                color: activeSection === section.id ? "#6652E4" : "#333",
                fontWeight: activeSection === section.id ? 600 : 400,
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "#f8f8ff",
                  transform: "translateX(-2px)",
                },
                mb: 0.5,
              }}
            >
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "inherit",
                  "& img": {
                    width: "16px",
                    height: "16px",
                    objectFit: "contain",
                  },
                  "& svg": {
                    width: "16px",
                    height: "16px",
                  },
                }}
              >
                {section.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: "14px", // Increased from "11px"
                  fontWeight: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                {section.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default BookmarkNavigation; 